import {
  apiGet,
  apiGetNoStore,
  apiGetWithFound,
  apiPost,
  apiPut,
  apiPatch,
  apiDelete,
  apiGetBlob,
  ApiError,
  publicGet,
  publicPost,
  submitCandidateApplication,
} from './httpClient';
import {
  clearActiveCountEndpointUnavailable,
  markActiveCountEndpointUnavailable,
} from '@/lib/activeCountAvailability';
import {
  clearArchivedCountEndpointUnavailable,
  markArchivedCountEndpointUnavailable,
} from '@/lib/archivedCountAvailability';

const PUBLIC_DOMAIN = (import.meta.env.VITE_PUBLIC_DOMAIN as string | undefined)?.replace(/\/$/, '') || '';
import { deriveScreeningQueued } from '@/lib/applicationScreening';
import type { JobPosition, JobPosting, Application, Interview, TeamMember, User, Company, CompanyTeam, CompanyLocalization, CompanyLink, AvailableAction, ScreeningReport, CandidateStructuredData, PipelineDetailRun, AiChatRequest, AiChatResponse, AiAutoFillRequest, AiAutoFillResponse, BillingStatus, BillingUsageMe, NotificationPreferences, JobPositionNotificationSettings } from '@/types';

export interface OnboardingEligibility {
  canCreate: boolean;
  reason?: 'sme_admin_exists' | 'agency_client_cap_reached' | string;
  existingCompany?: { id: string; name: string; planId: string | null };
  capInfo?: { used: number; cap: number; agencyPlanId: string };
  suggestedActions?: Array<'upgrade_sme' | 'upgrade_agency' | 'convert_to_agency' | 'contact_support'>;
}

// Helper to map backend position response to frontend shape
function mapPosition(raw: any): JobPosition {
  return {
    id: raw.id,
    companyId: raw.companyId,
    title: raw.title,
    description: raw.description || '',
    status: raw.status,
    seniority: raw.seniority,
    contractType: raw.contractType,
    workMode: raw.workMode,
    location: normalizeJobLocation(raw.location),
    salaryMin: raw.salaryRange?.min ?? raw.salaryMin,
    salaryMax: raw.salaryRange?.max ?? raw.salaryMax,
    salaryCurrency: raw.salaryRange?.currency ?? raw.salaryCurrency ?? 'EUR',
    budget: raw.budget,
    budgetCurrency: raw.budgetCurrency,
    team: raw.teamId ?? raw.team,
    requirePhone: raw.applicationFields?.requirePhone ?? raw.requirePhone,
    requireLinkedIn: raw.applicationFields?.requireLinkedin ?? raw.requireLinkedIn,
    notifyCandidate: raw.notifyCandidate,
    allowDuplicateApplications: raw.allowDuplicateApplications ?? false,
    applicationsCount: raw.applicationsCount ?? 0,
    initialPipelineKey: raw.initialPipelineKey,
    // Sprint 1 (D-R3.3 / BUG-10) — accept only canonical 'it' | 'en'.
    outputLanguage:
      raw.outputLanguage === 'it' || raw.outputLanguage === 'en'
        ? raw.outputLanguage
        : undefined,
    assignedMembers: Array.isArray(raw.subscribedMemberIds)
      ? raw.subscribedMemberIds
      : (Array.isArray(raw.assignedMembers) ? raw.assignedMembers : undefined),
    createdAt: raw.createdAt?._seconds ? new Date(raw.createdAt._seconds * 1000).toISOString() : raw.createdAt,
    updatedAt: raw.updatedAt?._seconds ? new Date(raw.updatedAt._seconds * 1000).toISOString() : raw.updatedAt,
  };
}

import { channelFromBackend, channelToBackend } from '@/lib/channels';
import { normalizeJobLocation } from '@/lib/utils';

function mapPosting(raw: any): JobPosting {
  return {
    id: raw.id,
    positionId: raw.jobPositionId || raw.positionId,
    positionTitle: raw.positionTitle || raw.title || '',
    title: raw.title,
    channel: channelFromBackend(raw.channel ?? 'other'),
    isActive: typeof raw.isActive === 'boolean' ? raw.isActive : true,
    landingPageUrl: raw.landingPageUrl || raw.publicUrl || '',
    applicationsCount: raw.applicationsCount ?? 0,
    publishedAt: raw.publishedAt?._seconds ? new Date(raw.publishedAt._seconds * 1000).toISOString() : (raw.publishedAt || raw.createdAt),
    createdAt: raw.createdAt?._seconds ? new Date(raw.createdAt._seconds * 1000).toISOString() : raw.createdAt,
    outputLanguage:
      raw.outputLanguage === 'it' || raw.outputLanguage === 'en'
        ? raw.outputLanguage
        : undefined,
  };
}

function mapTimestamp(ts: any): string {
  if (!ts) return new Date().toISOString();
  if (ts._seconds) return new Date(ts._seconds * 1000).toISOString();
  return ts;
}

function toPublicUrl(backendUrl: string): string {
  if (!PUBLIC_DOMAIN || !backendUrl) return backendUrl;
  try {
    const { pathname } = new URL(backendUrl);
    return `${PUBLIC_DOMAIN}${pathname}`;
  } catch {
    return backendUrl;
  }
}

function formatInterviewScheduledDateHint(scheduledIso: string): string {
  try {
    const d = new Date(scheduledIso);
    if (Number.isNaN(d.getTime())) return scheduledIso;
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return scheduledIso;
  }
}

function mapInterviewTypeLabel(raw: string | undefined): 'Video Call' | 'Phone' | 'In-person' {
  switch (raw) {
    case 'phone':
      return 'Phone';
    case 'in_person':
      return 'In-person';
    case 'video_call':
    default:
      return 'Video Call';
  }
}

function normalizeInterviewStatus(s: unknown): Interview['status'] {
  if (s === 'Updated' || s === 'Completed' || s === 'Scheduled' || s === 'Cancelled') return s;
  if (typeof s === 'string') {
    const lower = s.trim().toLowerCase();
    if (lower === 'updated') return 'Updated';
    if (lower === 'completed') return 'Completed';
    if (lower === 'scheduled') return 'Scheduled';
    if (lower === 'cancelled') return 'Cancelled';
  }
  return 'Scheduled';
}

/**
 * Same rules as backend `deriveInterviewDisplayStatus` / branch-2 mapping — prefer when `interviews[]`
 * is present but `status` is missing or stale (lists cached vs detail).
 */
function resolveInterviewStatusFromApi(
  int: Record<string, unknown>,
  raw: Record<string, unknown>,
): Interview['status'] {
  const scheduledIso = mapTimestamp(int.scheduledAt ?? int.scheduledDate);
  const durationM = (typeof int.duration === 'number' ? int.duration : undefined) ??
    (typeof int.durationMinutes === 'number' ? int.durationMinutes : undefined) ??
    60;
  const runId = (int.pipelineRunId as string | undefined) ?? (int.id as string | undefined);
  const runs = (raw.pipelineRuns as unknown[] | undefined) ?? [];
  const run = runs.find((r: unknown) => r && typeof r === 'object' && (r as { id?: string }).id === runId) as
    | Record<string, unknown>
    | undefined;
  if (run) {
    return deriveInterviewStatusFromRun(run, scheduledIso, durationM);
  }
  const startMs = new Date(scheduledIso).getTime();
  const endMs = startMs + durationM * 60 * 1000;
  if (!Number.isNaN(startMs) && endMs < Date.now()) return 'Completed';
  return normalizeInterviewStatus(int.status);
}

function deriveInterviewStatusFromRun(
  run: any,
  scheduledIso: string,
  durationMinutes: number,
): Interview['status'] {
  if (run.state === 'CANCELLED') return 'Cancelled';
  const startMs = new Date(scheduledIso).getTime();
  const endMs = startMs + durationMinutes * 60 * 1000;
  if (!Number.isNaN(startMs) && endMs < Date.now()) return 'Completed';
  const cSec = run.completedAt?._seconds ?? 0;
  const uSec = run.updatedAt?._seconds ?? 0;
  const completedMs = cSec * 1000;
  const updatedMs = uSec * 1000;
  if (completedMs > 0 && updatedMs > completedMs + 2000) return 'Updated';
  return 'Scheduled';
}

function mapInterviewsArray(raw: any, collected: Record<string, any>): Application['interviews'] {
  if (Array.isArray(raw.interviews) && raw.interviews.length > 0) {
    return raw.interviews.map((int: any) => ({
      id: int.id,
      pipelineRunId: int.pipelineRunId ?? int.id,
      applicationId: raw.id,
      scheduledAt: mapTimestamp(int.scheduledAt || int.scheduledDate),
      duration: int.duration || int.durationMinutes || 60,
      type: int.type || mapInterviewTypeLabel(int.interviewType),
      meetLink: int.meetLink || collected.meet_link?.value,
      calendarLink: int.calendarLink || collected.event_html_link?.value,
      calendarEventId: int.calendarEventId,
      participants: int.participants || [],
      notes: int.notes,
      status: resolveInterviewStatusFromApi(int, raw),
      transcript: int.transcript,
      reportSummary: int.reportSummary,
      enableTranscription: int.enableTranscription,
      createdAt: int.createdAt ? mapTimestamp(int.createdAt) : undefined,
      createdByName: int.createdByName || int.createdBy || undefined,
    }));
  }
  // Legacy: no interviews[] on API — derive from completed / cancelled interview pipeline runs
  const runs: any[] = (raw.pipelineRuns || []).filter(
    (r: any) => r.pipelineKey === 'interview' && (r.state === 'COMPLETED' || r.state === 'CANCELLED'),
  );
  if (runs.length === 0) return [];
  return runs.map((run: any) => {
    const inputs = run.decisionInputs || {};
    const scheduledAt = mapTimestamp(inputs.scheduledDate || run.completedAt || run.startedAt);
    const duration = inputs.durationMinutes || 60;
    const botSrc = collected.transcription_bot_id?.source?.pipelineRunId;
    const txStatus = collected.transcription_status?.value as string | undefined;
    const hasActiveBotForRun = botSrc === run.id && txStatus !== 'cancelled';
    const explicit = inputs.enableTranscription;
    const enableTranscription =
      explicit === false ? false : explicit === true ? true : hasActiveBotForRun;
    const suffixedTr = collected[`interview_transcript__${run.id}`];
    const legacyTr = collected.interview_transcript;
    const transcriptFromCollected =
      suffixedTr && (suffixedTr as { value?: unknown }).value != null
        ? String((suffixedTr as { value: unknown }).value)
        : legacyTr &&
            (legacyTr as { source?: { pipelineRunId?: string } }).source?.pipelineRunId === run.id
          ? String((legacyTr as { value?: unknown }).value ?? '')
          : undefined;
    const repEntry = collected.interview_report;
    let reportFromCollected: string | undefined;
    if (repEntry && typeof (repEntry as { value?: unknown }).value === 'string') {
      const srcId = (repEntry as { source?: { pipelineRunId?: string } }).source?.pipelineRunId;
      if (srcId === run.id) {
        reportFromCollected = (repEntry as { value: string }).value;
      } else if (srcId) {
        const ip = (raw.pipelineRuns || []).find(
          (pr: { id?: string; pipelineKey?: string; decisionInputs?: { source_pipeline_run_id?: string } }) =>
            pr.id === srcId && pr.pipelineKey === 'interview_processing',
        );
        if (ip?.decisionInputs?.source_pipeline_run_id === run.id) {
          reportFromCollected = (repEntry as { value: string }).value;
        }
      }
    }
    return {
      id: run.id,
      pipelineRunId: run.id,
      applicationId: raw.id,
      scheduledAt,
      duration,
      type: mapInterviewTypeLabel(inputs.interviewType),
      meetLink: collected.meet_link?.value,
      calendarLink: collected.event_html_link?.value,
      participants: [],
      notes: inputs.notes,
      status: deriveInterviewStatusFromRun(run, scheduledAt, duration),
      enableTranscription,
      createdAt: run.createdAt ? mapTimestamp(run.createdAt) : undefined,
      createdByName: run.createdByName || run.createdBy || undefined,
      ...(transcriptFromCollected ? { transcript: transcriptFromCollected } : {}),
      ...(reportFromCollected ? { reportSummary: reportFromCollected } : {}),
    };
  });
}

/** Map displayed interview type back to API decisionInputs.interviewType */
export function interviewTypeToApiValue(
  label: string,
): 'video_call' | 'phone' | 'in_person' {
  if (label === 'Phone') return 'phone';
  if (label === 'In-person') return 'in_person';
  return 'video_call';
}

function mapApplication(raw: any, companyId: string): Application {
  const collected = raw.collectedData || {};
  const aiScore = collected.screening_score?.value ?? raw.aiScore;
  const identityMismatchRaw = collected.form_resume_identity_mismatch?.value;
  const identityMismatch =
    identityMismatchRaw &&
    typeof identityMismatchRaw === 'object' &&
    (identityMismatchRaw as { hasMismatch?: boolean }).hasMismatch === true
      ? identityMismatchRaw
      : undefined;

  return {
    id: raw.id,
    companyId,
    positionId: raw.jobPositionId || raw.positionId,
    positionTitle: raw.positionTitle || raw.title || '',
    postingId: raw.jobPostingId || raw.postingId || '',
    channel: channelFromBackend(raw.channel ?? raw.source ?? 'other'),
    candidateFirstName: raw.candidateSnapshot?.firstName ?? raw.candidateFirstName ?? raw.candidate?.firstName ?? '',
    candidateLastName:  raw.candidateSnapshot?.lastName  ?? raw.candidateLastName  ?? raw.candidate?.lastName  ?? '',
    candidateEmail:     raw.candidateSnapshot?.email     ?? raw.candidateEmail     ?? raw.candidate?.email     ?? '',
    candidatePhone:     raw.candidateSnapshot?.phone     ?? raw.candidatePhone     ?? raw.candidate?.phone,
    state: raw.state,
    aiScore: typeof aiScore === 'number' ? aiScore : undefined,
    screeningReports: (() => {
      if (Array.isArray(raw.screeningReports) && raw.screeningReports.length > 0) {
        return raw.screeningReports;
      }
      // Top-level screeningReport field (legacy / v3 backend)
      const topLevelReport = raw.screeningReport;
      if (topLevelReport) {
        return [{
          id: 'sr-legacy-1',
          version: 1,
          generatedAt: raw.updatedAt || new Date().toISOString(),
          trigger: 'application' as const,
          triggerLabel: 'Initial screening',
          basedOnDocuments: [{ type: 'cv' as const, label: 'Resume' }],
          overallScore: topLevelReport.overallScore,
          scoreDelta: undefined,
          summary: topLevelReport.summary,
          // New format: {text, importance}[]. Legacy fallback: merge pros/cons strings as importance=2
          strengths: topLevelReport.strengths?.length
            ? topLevelReport.strengths
            : (topLevelReport.pros || []).map((p: string) => ({ text: p, importance: 2 as const })),
          weaknesses: topLevelReport.weaknesses?.length
            ? topLevelReport.weaknesses
            : (topLevelReport.cons || []).map((c: string) => ({ text: c, importance: 2 as const })),
          missingData: topLevelReport.missingData?.length && typeof topLevelReport.missingData[0] === 'object'
            ? topLevelReport.missingData
            : (topLevelReport.missingData || []).map((m: string) => ({ text: m, importance: 2 as const })),
          suggestedQuestions: topLevelReport.suggestedQuestions || [],
        }];
      }
      // collectedData fallback: pipeline outputs stored as { key: { value: ... } }
      const collected = raw.collectedData || {};
      const reportFromCollected = collected.screening_report?.value ?? collected.ai_report?.value ?? collected.screening_result?.value;
      if (reportFromCollected && typeof reportFromCollected === 'object') {
        return [{
          id: 'sr-collected-1',
          version: 1,
          generatedAt: mapTimestamp(raw.updatedAt),
          trigger: 'application' as const,
          triggerLabel: 'Initial screening',
          basedOnDocuments: [{ type: 'cv' as const, label: 'Resume' }],
          overallScore: reportFromCollected.overallScore ?? collected.screening_score?.value ?? 0,
          scoreDelta: undefined,
          summary: reportFromCollected.summary || '',
          // New format: {text, importance}[]. Legacy fallback: merge pros/cons strings as importance=2
          strengths: reportFromCollected.strengths?.length
            ? reportFromCollected.strengths
            : (reportFromCollected.pros || []).map((p: string) => ({ text: p, importance: 2 as const })),
          weaknesses: reportFromCollected.weaknesses?.length
            ? reportFromCollected.weaknesses
            : (reportFromCollected.cons || []).map((c: string) => ({ text: c, importance: 2 as const })),
          missingData: reportFromCollected.missingData?.length && typeof reportFromCollected.missingData[0] === 'object'
            ? reportFromCollected.missingData
            : (reportFromCollected.missingData || []).map((m: string) => ({ text: m, importance: 2 as const })),
          suggestedQuestions: reportFromCollected.suggestedQuestions || [],
        }];
      }
      return [];
    })(),
    cvUrl: raw.cvUrl || collected.cv_pdf_storage_path?.value || collected.resume_pdf_storage_path?.value,
    cvContent: raw.cvContent,
    structuredData: raw.structuredData
      ? normalizeCandidateStructured(raw.structuredData)
      : undefined,
    interviews: mapInterviewsArray(raw, collected),
    history: (() => {
      // Legacy: explicit history array
      const legacyHistory = raw.history || raw.stateHistory;
      if (Array.isArray(legacyHistory) && legacyHistory.length > 0) {
        return legacyHistory.map((h: any, i: number) => ({
          id: h.id || `hist-${i}`,
          fromState: h.fromState,
          toState: h.toState,
          event: h.event || h.eventName || 'State change',
          timestamp: mapTimestamp(h.timestamp),
          triggeredBy: h.triggeredBy || 'system',
          note: h.note,
        }));
      }
      // v5.0: derive from pipelineRuns (history[] removed — PipelineRun IS the audit trail)
      const runs: any[] = (raw.pipelineRuns || []).slice().sort((a: any, b: any) => {
        const ta = a.createdAt?._seconds ?? a.startedAt?._seconds ?? 0;
        const tb = b.createdAt?._seconds ?? b.startedAt?._seconds ?? 0;
        return ta - tb;
      });
      // Map pipelineKey → human-readable label + implied toState
      const PIPELINE_LABEL: Record<string, string> = {
        screening:       'Initial AI Screening',
        application:     'Application Received',
        interview:       'Interview Pipeline',
        tech_test:       'Tech Test Pipeline',
        offer:           'Offer Pipeline',
        rejection:       'Rejection',
        hire:            'Hired',
        withdrawal:      'Withdrawal',
        offer_declined:  'Offer Declined',
      };
      const PIPELINE_TERMINAL_STATE: Record<string, string> = {
        rejection:      'REJECTED',
        hire:           'HIRED',
        withdrawal:     'WITHDRAWN',
        offer_declined: 'OFFER_DECLINED',
      };
      return runs.map((run: any, i: number) => {
        let label = PIPELINE_LABEL[run.pipelineKey] ?? run.pipelineKey;
        let note: string | undefined;
        const isCancelled = run.state === 'CANCELLED' || run.outcome === 'cancelled';
        if (run.pipelineKey === 'interview' && isCancelled) {
          label = 'Interview cancelled';
          note = 'Interview was removed from calendar; attendees may have been notified.';
        } else if (run.pipelineKey === 'interview') {
          const sched = run.decisionInputs?.scheduledDate as string | undefined;
          if (sched && run.outcome !== 'failed') {
            note = `Latest slot: ${formatInterviewScheduledDateHint(sched)}`;
          }
        }
        const toState: string = run.terminalState
          ?? PIPELINE_TERMINAL_STATE[run.pipelineKey]
          ?? (run.outcome === 'failed' ? 'PIPELINE_ERROR' : (i === runs.length - 1 ? raw.state : 'AWAITING_DECISION'));
        if (note === undefined) {
          note = run.outcome === 'failed'
            ? 'Pipeline failed'
            : run.outcome === 'skipped'
              ? 'No steps — completed instantly'
              : undefined;
        }
        if (run.pipelineKey === 'interview' && run.outcome === 'completed') {
          const calendarNote = 'Calendar event created — awaiting your next hiring step';
          note = note ? `${calendarNote} · ${note}` : calendarNote;
        }
        return {
          id: run.id,
          fromState: null as any,
          toState,
          event: label,
          timestamp: mapTimestamp(run.completedAt ?? run.startedAt ?? run.createdAt),
          triggeredBy:
            isCancelled && run.pipelineKey === 'interview'
              ? 'system:interviewCancelled'
              : run.decidedBy === 'system'
                ? 'system'
                : (run.decidedBy ?? 'system'),
          note,
        };
      });
    })(),
    appliedAt: mapTimestamp(raw.appliedAt),
    updatedAt: mapTimestamp(raw.updatedAt),
    collectedData: collected,
    ...(identityMismatch ? { hasIdentityMismatch: true, identityMismatch } : {}),
    ...(typeof raw.hasScheduledInterview === 'boolean' ? { hasScheduledInterview: raw.hasScheduledInterview } : {}),
    ...(raw.lastFailureReason ? { lastFailureReason: raw.lastFailureReason } : {}),
    ...(raw.deferredPipeline ? { deferredPipeline: raw.deferredPipeline } : {}),
    ...(raw.archived === true ? { archived: true } : {}),
    ...(raw.archivedAt ? { archivedAt: mapTimestamp(raw.archivedAt) } : {}),
    ...(raw.archivedBy ? { archivedBy: raw.archivedBy } : {}),
    screeningQueued: deriveScreeningQueued(raw.state, raw.pipelineRuns),
  };
}

function mapTeamMember(raw: any): TeamMember {
  const uid = raw.authUid || raw.userId || raw.id || '';
  // Normalize permission — BE sends 'admin'|'write'|'read'
  const rawPerm = raw.permission || raw.role;
  const permission: TeamMember['permission'] =
    rawPerm === 'admin' ? 'admin' :
    rawPerm === 'write' || rawPerm === 'recruiter' ? 'write' :
    'read';

  // Pending invitations have status='pending' set explicitly by BE
  const status: TeamMember['status'] =
    raw.status === 'pending' ? 'pending' :
    raw.status === 'declined' ? 'declined' :
    raw.isActive === false ? 'inactive' :
    'active';

  return {
    id: raw.id || uid,
    userId: uid,
    displayName: raw.displayName || raw.email || '',
    email: raw.email || '',
    permission,
    jobRole: raw.jobRole ?? null,
    type: raw.type || 'internal',
    status,
    teamIds: Array.isArray(raw.teamIds) ? raw.teamIds : (raw.teamId ? [raw.teamId] : []),
    avatarUrl: raw.avatarUrl,
    joinedAt: mapTimestamp(raw.joinedAt || raw.invitedAt || raw.createdAt),
    showInSuggestions: raw.showInSuggestions ?? false,
  };
}

// Safely extract array from paginated or plain responses
function extractItems(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  return raw.items || raw.data || [];
}

/**
 * normalizeCandidateStructured — bridges BE CV schema to FE CandidateStructuredData.
 *
 * BE (StructuredCVDataSchema) uses workExperience, linkedinUrl, etc.
 * FE uses experience, linkedIn, etc. Also unwraps legacy extractedData wrapper.
 */
function normalizeCandidateStructured(raw: unknown): CandidateStructuredData {
  const root = (raw ?? {}) as Record<string, unknown>;
  const data = (
    root['extractedData'] && typeof root['extractedData'] === 'object'
      ? root['extractedData']
      : root
  ) as Record<string, unknown>;

  const rawExperience = (
    data['experience']
    ?? data['workExperience']
    ?? data['experiences']
    ?? []
  ) as unknown[];

  const experience = Array.isArray(rawExperience)
    ? rawExperience.map((entry) => {
        const e = (entry ?? {}) as Record<string, unknown>;
        const role = String(e['role'] ?? e['title'] ?? e['position'] ?? '').trim();
        const company = String(e['company'] ?? e['employer'] ?? e['organization'] ?? '').trim();
        return {
          role,
          company,
          startDate: String(e['startDate'] ?? e['start'] ?? '').trim(),
          endDate: (() => {
            const end = e['endDate'] ?? e['end'];
            return end != null && String(end).trim() !== '' ? String(end).trim() : undefined;
          })(),
          description: e['description'] != null ? String(e['description']) : undefined,
        };
      }).filter(e => e.role || e.company)
    : [];

  const rawEducation = Array.isArray(data['education']) ? (data['education'] as unknown[]) : [];
  const education = rawEducation.map((entry) => {
    const ed = (entry ?? {}) as Record<string, unknown>;
    return {
      institution: String(ed['institution'] ?? ed['school'] ?? '').trim(),
      degree: String(ed['degree'] ?? '').trim(),
      field: String(ed['field'] ?? ed['fieldOfStudy'] ?? '').trim(),
      startDate: String(ed['startDate'] ?? ed['start'] ?? '').trim(),
      endDate: ed['endDate'] != null || ed['end'] != null
        ? String(ed['endDate'] ?? ed['end'] ?? '').trim()
        : undefined,
    };
  }).filter(ed => ed.institution || ed.degree);

  const langs = Array.isArray(data['languages']) ? (data['languages'] as unknown[]) : [];
  const languages = langs.map((l) => {
    const obj = (l ?? {}) as Record<string, unknown>;
    return {
      name: String(obj['name'] ?? obj['language'] ?? '').trim(),
      proficiency: String(obj['proficiency'] ?? obj['level'] ?? '').trim(),
    };
  }).filter(l => l.name);

  const skills = Array.isArray(data['skills'])
    ? (data['skills'] as unknown[]).map(s => String(s).trim()).filter(Boolean)
    : [];

  const certifications = Array.isArray(data['certifications'])
    ? (data['certifications'] as unknown[]).map(c => String(c).trim()).filter(Boolean)
    : [];

  return {
    firstName: data['firstName'] != null ? String(data['firstName']) : undefined,
    lastName: data['lastName'] != null ? String(data['lastName']) : undefined,
    email: data['email'] != null ? String(data['email']) : undefined,
    phone: data['phone'] != null ? String(data['phone']) : undefined,
    linkedIn: (data['linkedIn'] ?? data['linkedinUrl'] ?? data['linkedin']) != null
      ? String(data['linkedIn'] ?? data['linkedinUrl'] ?? data['linkedin'])
      : undefined,
    github: (data['github'] ?? data['githubUrl']) != null
      ? String(data['github'] ?? data['githubUrl'])
      : undefined,
    portfolio: (data['portfolio'] ?? data['portfolioUrl']) != null
      ? String(data['portfolio'] ?? data['portfolioUrl'])
      : undefined,
    currentRole: data['currentRole'] != null ? String(data['currentRole']) : undefined,
    currentCompany: data['currentCompany'] != null ? String(data['currentCompany']) : undefined,
    yearsOfExperience: typeof data['yearsOfExperience'] === 'number'
      ? data['yearsOfExperience']
      : undefined,
    experience,
    education,
    skills,
    languages,
    certifications,
    summary: data['summary'] != null ? String(data['summary']) : undefined,
  };
}

type BulkArchivePayload = Array<{ applicationId: string; positionId: string }>;

async function bulkArchiveFallback(
  companyId: string,
  applications: BulkArchivePayload,
): Promise<{ updated: number; errors: number; total: number }> {
  const results = await Promise.allSettled(
    applications.map(({ applicationId, positionId }) =>
      apiPost(`/companies/${companyId}/job-positions/${positionId}/applications/${applicationId}/archive`, {}),
    ),
  );
  const updated = results.filter((r) => r.status === 'fulfilled').length;
  return { updated, errors: results.length - updated, total: applications.length };
}

async function bulkUnarchiveFallback(
  companyId: string,
  applications: BulkArchivePayload,
): Promise<{ updated: number; errors: number; total: number }> {
  const results = await Promise.allSettled(
    applications.map(({ applicationId, positionId }) =>
      apiPost(`/companies/${companyId}/job-positions/${positionId}/applications/${applicationId}/unarchive`, {}),
    ),
  );
  const updated = results.filter((r) => r.status === 'fulfilled').length;
  return { updated, errors: results.length - updated, total: applications.length };
}

export const api = {
  auth: {
    getCurrentUser: async (): Promise<User> => {
      return apiGet<User>('/users/me');
    },
    updateProfile: async (data: {
      displayName?: string;
      avatarUrl?: string;
      timeZone?: string;
      preferredLanguage?: 'en' | 'it';
      lastSelectedCompanyId?: string;
      accountType?: 'agency_owner';
      agencyPlanId?: 'agency_starter' | 'agency_professional';
      agencyBillingPeriod?: 'monthly' | 'annual';
    }): Promise<User> => {
      return apiPut<User>('/users/me', data);
    },
    disableAccount: async (): Promise<void> => {
      return apiDelete<void>('/users/me');
    },
    deleteAccount: async (): Promise<void> => {
      return apiDelete<void>('/users/me/permanent');
    },
    /**
     * v3.4 (25/05/26) — leave a company membership (self-service).
     * Used by the "Lascia azienda" button in profile/Aziende tab.
     * Returns 204 No Content on success.
     * 403 if the user is the company owner or last admin.
     */
    leaveCompany: async (companyId: string): Promise<void> => {
      return apiDelete<void>(`/users/me/memberships/${companyId}`);
    },
    getOnboardingEligibility: async (): Promise<OnboardingEligibility> => {
      return apiGet<OnboardingEligibility>('/users/me/onboarding/eligibility');
    },
    sendPasswordReset: async (email: string): Promise<{ ok: true }> => {
      return publicPost<{ ok: true }>('/auth/send-password-reset', { email });
    },
    /**
     * v3-notif (27/05/26) — User notification preferences (FASE 0).
     *
     * Returns the effective prefs with system defaults applied when the user
     * has never opened the panel. Detect "using defaults" via
     * `updatedAt._seconds === 0` for the FE banner.
     */
    getNotificationPreferences: async (): Promise<NotificationPreferences> => {
      return apiGet<NotificationPreferences>('/users/me/notification-preferences');
    },
    /**
     * Full upsert. The FE always sends the complete object (read-modify-write).
     */
    putNotificationPreferences: async (
      prefs: Omit<NotificationPreferences, 'updatedAt' | 'schemaVersion'>,
    ): Promise<NotificationPreferences> => {
      return apiPut<NotificationPreferences>('/users/me/notification-preferences', prefs);
    },
    /**
     * Resets the preferences to system defaults by deleting the persisted doc.
     * Next read returns defaults with the epoch-0 sentinel `updatedAt`.
     */
    resetNotificationPreferences: async (): Promise<void> => {
      return apiDelete<void>('/users/me/notification-preferences');
    },
    submitSupportReport: async (data: {
      reportId: string;
      category: 'bug' | 'feedback' | 'other';
      message: string;
      diagnostics: Record<string, unknown>;
      attachmentUrls: string[];
      locale: 'it' | 'en';
    }): Promise<{ ok: true; reportId: string }> => {
      return apiPost<{ ok: true; reportId: string }>('/users/me/support-reports', data);
    },
  },

  companies: {
    list: async (): Promise<Company[]> => {
      const raw = await apiGet<any>('/users/me/companies');
      const items = Array.isArray(raw) ? raw : (raw.items || raw.data || raw.memberships || []);
      return items.map((c: any) => ({
        id: c.id || c.companyId,
        name: c.name || c.companyName || '',
        publicName: c.publicName || c.name || '',
        slug: c.slug || '',
        description: c.description,
        logoUrl: c.logoUrl || c.branding?.logoUrl,
      }));
    },
    get: async (id: string): Promise<Company | undefined> => {
      try {
        const raw = await apiGet<any>(`/companies/${id}`);
        return {
          id: raw.id,
          name: raw.name || '',
          publicName: raw.publicName || raw.name || '',
          slug: raw.slug || '',
          description: raw.description,
          logoUrl: raw.logoUrl || raw.branding?.logoUrl,
          employeeCount: raw.employeeCount,
          localization: raw.localization,
          links: raw.links,
          salary: raw.salary ?? raw.averageSalary,
          // Sprint 1 (D-R3.3 / BUG-10)
          defaultOutputLanguage:
            raw.defaultOutputLanguage === 'it' || raw.defaultOutputLanguage === 'en'
              ? raw.defaultOutputLanguage
              : undefined,
        };
      } catch {
        return undefined;
      }
    },
    create: async (data: Partial<Company>): Promise<Company> => {
      return apiPost<Company>('/companies', {
        name: data.name,
        publicName: data.publicName,
        description: data.description,
      });
    },
    update: async (id: string, data: {
      name?: string;
      publicName?: string;
      description?: string;
      employeeCount?: number;
      salary?: { min?: number; max?: number; currency?: string };
      localization?: CompanyLocalization[];
      links?: CompanyLink[];
      /** `null` clears the logo on the BE (mapped to FieldValue.delete) — Plan B Phase 1. */
      logoUrl?: string | null;
      /**
       * Sprint 1 (D-R3.3 / BUG-10) — default AI/email language inherited by
       * future positions. Existing positions keep their explicit override.
       */
      defaultOutputLanguage?: 'it' | 'en';
    }): Promise<Company> => {
      // Strip empty/invalid values — backend description has min(10), empty string fails validation
      const body: Record<string, unknown> = {};
      if (data.name) body['name'] = data.name;
      if (data.publicName) body['publicName'] = data.publicName;
      if (data.description && data.description.trim().length >= 10) body['description'] = data.description.trim();
      if (data.employeeCount !== undefined && data.employeeCount > 0) body['employeeCount'] = data.employeeCount;
      if (data.salary) body['salary'] = data.salary;
      if (data.localization !== undefined) body['localization'] = data.localization;
      if (data.links !== undefined) body['links'] = data.links;
      // Forward both URL set ('') and explicit clear (null) to the BE.
      if (data.logoUrl !== undefined) body['logoUrl'] = data.logoUrl;
      if (data.defaultOutputLanguage) body['defaultOutputLanguage'] = data.defaultOutputLanguage;
      return apiPut<Company>(`/companies/${id}`, body);
    },
    delete: async (id: string): Promise<void> => {
      await apiDelete(`/companies/${id}/permanent`);
    },
  },

  billing: {
    getStatus: async (companyId: string): Promise<BillingStatus> => {
      return apiGet<BillingStatus>(`/companies/${companyId}/billing/status`);
    },
    /** Call after returning from Stripe Customer Portal to refresh Autumn + Firestore plan. Uses GET (POST also supported on server). */
    reconcile: async (companyId: string): Promise<BillingStatus> => {
      return apiGetNoStore<BillingStatus>(`/companies/${companyId}/billing/reconcile`);
    },
    checkout: async (
      companyId: string,
      body: {
        planId: string;
        billingPeriod?: 'monthly' | 'annual';
        successUrl?: string;
        /**
         * v3.2 (21/05/26) — Agency chooser quantity. For `agency_*` planIds,
         * sets the Stripe subscription quantity to the number of managed
         * companies the user is pre-buying. BE enforces the per-tier band
         * (Starter 1-5, Medium 6-10, Pro 11-20).
         */
        desiredCompanies?: number;
        /**
         * Sprint pricing_v3_5_production_rollout follow-up (26/05/26 23:15).
         * Stripe promotion code (human-readable, created in Stripe
         * Dashboard, e.g. "TEST99"). When passed, Autumn forwards it
         * to the Stripe Checkout session as a pre-applied discount.
         * Used for QA/testing in live without burning real money.
         */
        promotionCode?: string;
      }
    ): Promise<{ checkoutUrl: string }> => {
      return apiPost<{ checkoutUrl: string }>(`/companies/${companyId}/billing/checkout`, body);
    },
    getPortalUrl: async (companyId: string, returnUrl?: string): Promise<{ url: string }> => {
      const q = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
      return apiGet<{ url: string }>(`/companies/${companyId}/billing/portal${q}`);
    },
    cancel: async (
      companyId: string,
      body: { planId: string; immediate?: boolean }
    ): Promise<void> => {
      return apiPost<void>(`/companies/${companyId}/billing/cancel`, body);
    },
    /**
     * @deprecated v3.5 (25/05/26) — endpoint /billing/usage/me deprecato e
     * rimosso dal BE in PR2. Nessun consumer FE attivo. Mantenuto come stub
     * che ritorna `null` per non rompere import legacy.
     */
    getUsageMe: async (_companyId: string): Promise<BillingUsageMe | null> => {
      return Promise.resolve(null);
    },
    getTrial: async (companyId: string): Promise<import('@/types/billing').TrialContext> => {
      return apiGet<import('@/types/billing').TrialContext>(`/companies/${companyId}/billing/trial`);
    },
    listPlans: async (
      companyId: string,
      params?: { segment?: 'sme' | 'agency' | 'all' }
    ): Promise<import('@/types/billing').PlansResponse> => {
      const q = params?.segment ? `?segment=${params.segment}` : '';
      return apiGet<import('@/types/billing').PlansResponse>(`/companies/${companyId}/billing/plans${q}`);
    },
    // ─── v3.2 (21/05/26) user-level billing endpoints ───────────────────
    //
    // These complement the company-scoped endpoints above. Used by the
    // FE Account & Piano section (Fase 4). Checkout / portal / cancel /
    // topup continue to use the company-scoped endpoints, which now route
    // owner-first via the BE Fase 3.1 pivot.
    getMyStatus: async (): Promise<import('@/types/billing').UserBillingStatus> => {
      return apiGet<import('@/types/billing').UserBillingStatus>(`/users/me/billing/status`);
    },
    listMyPlans: async (): Promise<import('@/types/billing').UserPlansResponse> => {
      return apiGet<import('@/types/billing').UserPlansResponse>(`/users/me/billing/plans`);
    },
    /**
     * v3.2 polish (21/05/26 ~22:00) — agency pay-as-you-grow preview.
     * Calls BE `previewAgencySeatBump(uid, ownedCount+1)` to show the
     * prorated charge in the FE confirm modal BEFORE the user lands on
     * the create-company form (where the actual addebito happens at
     * POST /companies time).
     *
     * Throws ApiError (400) when no active agency sub or when the
     * target quantity exceeds the tier band.
     */
    previewAddCompanyForAgency: async (): Promise<{
      currentQuantity: number;
      newQuantity: number;
      delta: number;
      pricePerSeat: number;
      proratedNow: number;
      newMonthlyTotal: number;
      currency: string;
      agencyPlanId: 'agency_starter' | 'agency_medium' | 'agency_professional';
    }> => {
      return apiPost(`/users/me/agency/preview-add-company`, {});
    },
  },

  // Teams are a sub-collection: /companies/:cid/teams/:teamId
  companyTeams: {
    list: async (companyId: string): Promise<CompanyTeam[]> => {
      const raw = await apiGet<any>(`/companies/${companyId}/teams`);
      const items = Array.isArray(raw) ? raw : (raw.items || raw.data || []);
      return items.map((t: any): CompanyTeam => ({
        id: t.id,
        name: t.name,
        description: t.description,
        memberIds: Array.isArray(t.memberIds) ? t.memberIds : [],
      }));
    },
    listMembers: async (companyId: string, teamId: string): Promise<TeamMember[]> => {
      const raw = await apiGet<any>(`/companies/${companyId}/teams/${teamId}/members`);
      return extractItems(raw).map(mapTeamMember);
    },
    create: async (companyId: string, data: { name: string; description?: string }): Promise<CompanyTeam> => {
      const raw = await apiPost<any>(`/companies/${companyId}/teams`, data);
      return { id: raw.id, name: raw.name, description: raw.description, memberIds: [] };
    },
    update: async (companyId: string, teamId: string, data: { name?: string; description?: string }): Promise<CompanyTeam> => {
      const raw = await apiPut<any>(`/companies/${companyId}/teams/${teamId}`, data);
      return { id: raw.id, name: raw.name, description: raw.description };
    },
    delete: async (companyId: string, teamId: string): Promise<void> => {
      await apiDelete(`/companies/${companyId}/teams/${teamId}`);
    },
  },

  positions: {
    listPage: async (
      companyId: string,
      options?: { limit?: number; cursor?: string },
    ): Promise<{ items: JobPosition[]; hasMore: boolean; nextCursor?: string }> => {
      const params = new URLSearchParams();
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.cursor) params.set('cursor', options.cursor);
      const qs = params.toString();
      const raw = await apiGet<any>(`/companies/${companyId}/job-positions${qs ? `?${qs}` : ''}`);
      const items = (raw.items ?? extractItems(raw)).map(mapPosition);
      return {
        items,
        hasMore: !!raw.hasMore,
        ...(raw.nextCursor ? { nextCursor: raw.nextCursor as string } : {}),
      };
    },
    list: async (companyId: string): Promise<JobPosition[]> => {
      const all: JobPosition[] = [];
      let cursor: string | undefined;
      let hasMore = true;
      while (hasMore) {
        const params = new URLSearchParams({ limit: '50' });
        if (cursor) params.set('cursor', cursor);
        const raw = await apiGet<any>(`/companies/${companyId}/job-positions?${params.toString()}`);
        const items = (raw.items ?? extractItems(raw)).map(mapPosition);
        all.push(...items);
        hasMore = !!raw.hasMore;
        cursor = raw.nextCursor as string | undefined;
      }
      return all;
    },
    get: async (companyId: string, id: string): Promise<JobPosition | undefined> => {
      try {
        const raw = await apiGet<any>(`/companies/${companyId}/job-positions/${id}`);
        return mapPosition(raw);
      } catch {
        return undefined;
      }
    },
    create: async (
      companyId: string,
      data: Partial<JobPosition>,
    ): Promise<{ position: JobPosition; postingUrl: string | null }> => {
       const body: any = {
         title: data.title,
         description: data.description,
         seniority: data.seniority,
         contractType: data.contractType,
         workMode: data.workMode,
         location: data.location,
         notifyCandidate: data.notifyCandidate,
         allowDuplicateApplications: data.allowDuplicateApplications ?? false,
       };
       // teamId is optional — omit entirely when not set (empty string or undefined)
       if (data.team) body.teamId = data.team;
       if (data.salaryMin || data.salaryMax) {
         body.salaryRange = { min: data.salaryMin, max: data.salaryMax, currency: data.salaryCurrency || 'EUR' };
       }
       if (data.requirePhone !== undefined || data.requireLinkedIn !== undefined) {
         body.applicationFields = { requirePhone: data.requirePhone, requireLinkedin: data.requireLinkedIn };
       }
       // memberIds: notify subscribers — creator is auto-added by backend
       if (data.assignedMembers?.length) {
         body.memberIds = data.assignedMembers;
       }
       if (data.pipelineTemplateId) body.pipelineTemplateId = data.pipelineTemplateId;
       if (data.initialPipelineKey) body.initialPipelineKey = data.initialPipelineKey;
       // Sprint 1 (D-R3.3 / BUG-10): forward only when explicitly set so the
       // BE inheritance from `Company.defaultOutputLanguage` keeps working
       // when the recruiter doesn't touch the language selector.
       if (data.outputLanguage === 'it' || data.outputLanguage === 'en') {
         body.outputLanguage = data.outputLanguage;
       }
       const raw = await apiPost<any>(`/companies/${companyId}/job-positions`, body);
       // Sprint 3 (S3.E): the BE service returns `{ position, posting }`
       // since v1.2 — we pull the posting's `landingPageUrl` so the
       // post-create wizard can show + open the public link without an
       // extra round-trip. Backward compatible: legacy responses that
       // serialise just the position still work via `raw.position || raw`.
       const positionData = raw?.position ?? raw;
       const posting = raw?.posting ?? null;
       const postingUrl = posting?.landingPageUrl ? toPublicUrl(posting.landingPageUrl) : null;
       return { position: mapPosition(positionData), postingUrl };
    },
    update: async (companyId: string, id: string, data: Partial<JobPosition>): Promise<JobPosition> => {
       const body: any = {
         title: data.title,
         description: data.description,
         seniority: data.seniority,
         contractType: data.contractType,
         workMode: data.workMode,
         location: data.location,
         notifyCandidate: data.notifyCandidate,
         allowDuplicateApplications: data.allowDuplicateApplications ?? false,
       };
       // teamId is optional — omit entirely when not set (empty string or undefined)
       if (data.team) body.teamId = data.team;
       if (data.salaryMin || data.salaryMax) {
         body.salaryRange = { min: data.salaryMin, max: data.salaryMax, currency: data.salaryCurrency || 'EUR' };
       }
       if (data.requirePhone !== undefined || data.requireLinkedIn !== undefined) {
         body.applicationFields = { requirePhone: data.requirePhone, requireLinkedin: data.requireLinkedIn };
       }
       if (data.assignedMembers !== undefined) {
         body.memberIds = data.assignedMembers;
       }
       // Sprint 1 (D-R3.3 / BUG-10)
       if (data.outputLanguage === 'it' || data.outputLanguage === 'en') {
         body.outputLanguage = data.outputLanguage;
       }
       const raw = await apiPut<any>(`/companies/${companyId}/job-positions/${id}`, body);
      return mapPosition(raw);
    },
    updateStatus: async (companyId: string, id: string, status: string): Promise<void> => {
      await apiPatch(`/companies/${companyId}/job-positions/${id}/status`, { status });
    },
    delete: async (companyId: string, id: string): Promise<void> => {
      await apiDelete(`/companies/${companyId}/job-positions/${id}`);
    },
    permanentDelete: async (companyId: string, id: string): Promise<void> => {
      await apiDelete(`/companies/${companyId}/job-positions/${id}/permanent`);
    },

    /**
     * v3-notif (27/05/26) — Position notification settings (FASE 1).
     *
     * Any member can read. The BE applies system defaults with epoch-0
     * `updatedAt` sentinel when the position has no explicit field —
     * detect via `updatedAt._seconds === 0` to render "using defaults" UX.
     */
    getNotificationSettings: async (
      companyId: string,
      positionId: string,
    ): Promise<JobPositionNotificationSettings> => {
      return apiGet<JobPositionNotificationSettings>(
        `/companies/${companyId}/job-positions/${positionId}/notification-settings`,
      );
    },
    /**
     * write/admin only (BE gate via `companyAccess('write')`). Full upsert
     * — FE always sends the complete object.
     */
    updateNotificationSettings: async (
      companyId: string,
      positionId: string,
      settings: Pick<
        JobPositionNotificationSettings,
        'emitApplicationReceived' | 'emitApplicationScreened' | 'emitApplicationState' | 'emitInterviewLifecycle' | 'enableDailyDigest'
      >,
    ): Promise<JobPositionNotificationSettings> => {
      return apiPatch<JobPositionNotificationSettings>(
        `/companies/${companyId}/job-positions/${positionId}/notification-settings`,
        settings,
      );
    },
  },

  postings: {
    listByPosition: async (companyId: string, positionId: string): Promise<JobPosting[]> => {
      const raw = await apiGet<any>(`/companies/${companyId}/job-positions/${positionId}/postings`);
      return extractItems(raw).map(mapPosting);
    },
    listAll: async (companyId: string, positions: JobPosition[]): Promise<JobPosting[]> => {
      const results = await Promise.all(
        positions.map(pos =>
          apiGet<any>(`/companies/${companyId}/job-positions/${pos.id}/postings`)
            .then(raw => extractItems(raw).map((r: any) => mapPosting({ ...r, positionTitle: pos.title })))
            .catch(() => [] as JobPosting[])
        )
      );
      return results.flat();
    },
    create: async (companyId: string, positionId: string, data: { channel: string; title?: string }): Promise<JobPosting> => {
      const raw = await apiPost<any>(`/companies/${companyId}/job-positions/${positionId}/postings`, {
        ...data,
        channel: channelToBackend(data.channel),
      });
      return mapPosting(raw);
    },
    update: async (companyId: string, positionId: string, postingId: string, data: { channel?: string; title?: string }): Promise<JobPosting> => {
      const body: any = { ...data };
      if (data.channel) body.channel = channelToBackend(data.channel);
      const raw = await apiPut<any>(`/companies/${companyId}/job-positions/${positionId}/postings/${postingId}`, body);
      return mapPosting(raw);
    },
    // Backend: PATCH .../close → isActive false; PATCH .../reopen → isActive true.
    closePosting: async (companyId: string, positionId: string, postingId: string): Promise<JobPosting> => {
      const raw = await apiPatch<any>(`/companies/${companyId}/job-positions/${positionId}/postings/${postingId}/close`, {});
      return mapPosting(raw);
    },
    reopenPosting: async (companyId: string, positionId: string, postingId: string): Promise<JobPosting> => {
      const raw = await apiPatch<any>(`/companies/${companyId}/job-positions/${positionId}/postings/${postingId}/reopen`, {});
      return mapPosting(raw);
    },
  },

  applications: {
    /**
     * Returns ALL applications for a position by paginating through the
     * cursor-based BE endpoint until exhausted. Each page is capped at
     * 100 (BE ListApplicationsQuerySchema max).
     *
     * Background: the BE GET /companies/:cid/job-positions/:pid/applications
     * applies a default limit of 20 when none is specified, so the previous
     * single-call implementation returned only the first 20 — both the
     * candidate table and the AI prompt context were silently truncated.
     */
    listByPosition: async (
      companyId: string,
      positionId: string,
      options?: { archived?: boolean; orderBy?: 'appliedAt' | 'updatedAt' },
    ): Promise<Application[]> => {
      const all: Application[] = [];
      let cursor: string | undefined;
      let hasMore = true;
      // BE caps page size at 100 (ListApplicationsQuerySchema). Loop until
      // exhausted — hard stop at 100 pages × 100 = 10 000 candidates/position.
      let safety = 100;
      while (hasMore && safety-- > 0) {
        const qs = new URLSearchParams({
          limit: '100',
          orderBy: options?.orderBy ?? 'updatedAt',
        });
        if (options?.archived === true) qs.set('archived', 'true');
        else if (options?.archived === false) qs.set('archived', 'false');
        if (cursor) qs.set('cursor', cursor);
        const raw = await apiGet<any>(
          `/companies/${companyId}/job-positions/${positionId}/applications?${qs.toString()}`,
        );
        const items = extractItems(raw).map((r: any) =>
          mapApplication({ ...r, jobPositionId: r.jobPositionId ?? positionId }, companyId),
        );
        all.push(...items);
        hasMore = !!raw.hasMore;
        cursor = raw.nextCursor as string | undefined;
      }
      return all;
    },
    listAll: async (companyId: string, positions: JobPosition[]): Promise<Application[]> => {
      const results = await Promise.all(
        positions.filter(p => p.status === 'active' || p.status === 'closed').map(pos =>
          apiGet<any>(`/companies/${companyId}/job-positions/${pos.id}/applications`)
            .then(raw => extractItems(raw).map((r: any) => mapApplication({ ...r, positionTitle: pos.title }, companyId)))
            .catch(() => [] as Application[])
        )
      );
      return results.flat();
    },
    /**
     * Sprint 5 (S5.C) — cursor-paginated cross-position list.
     *
     * Single BE call replaces the legacy `listAll(positions)` N+1 pattern.
     * The `positions` arg is still accepted so callers can hydrate
     * `positionTitle` on the items in memory (the BE doesn't denormalize
     * it; mapApplication walks an internal lookup).
     */
    listByCompany: async (
      companyId: string,
      positions: JobPosition[],
      options?: {
        limit?: number;
        cursor?: string;
        state?: string;
        jobPositionId?: string;
        archived?: boolean;
        orderBy?: 'appliedAt' | 'updatedAt';
      },
    ): Promise<{ items: Application[]; hasMore: boolean; nextCursor?: string }> => {
      const params = new URLSearchParams();
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.cursor) params.set('cursor', options.cursor);
      if (options?.state) params.set('state', options.state);
      if (options?.jobPositionId) params.set('jobPositionId', options.jobPositionId);
      if (options?.archived === true) params.set('archived', 'true');
      else if (options?.archived === false) params.set('archived', 'false');
      if (options?.orderBy) params.set('orderBy', options.orderBy);
      const qs = params.toString();
      const raw = await apiGet<any>(`/companies/${companyId}/applications${qs ? `?${qs}` : ''}`);
      const titleByPosition = positions.reduce<Record<string, string>>((acc, p) => {
        acc[p.id] = p.title;
        return acc;
      }, {});
      const items = (raw.items ?? []).map((r: any) => {
        const positionId = r.jobPositionId ?? r.positionId;
        return mapApplication(
          {
            ...r,
            positionTitle: titleByPosition[positionId] ?? r.positionTitle ?? r.title ?? '',
          },
          companyId,
        );
      });
      return {
        items,
        hasMore: !!raw.hasMore,
        ...(raw.nextCursor ? { nextCursor: raw.nextCursor } : {}),
      };
    },
    /** Fetches every page from the cross-position cursor endpoint. */
    listAllByCompany: async (
      companyId: string,
      positions: JobPosition[],
      options?: { archived?: boolean },
    ): Promise<Application[]> => {
      const all: Application[] = [];
      let cursor: string | undefined;
      let hasMore = true;
      // BE caps page size at 100; use max pages to avoid silent truncation.
      let safety = 150;
      while (hasMore && safety-- > 0) {
        const page = await api.applications.listByCompany(companyId, positions, {
          limit: 100,
          cursor,
          archived: options?.archived ?? false,
          orderBy: 'updatedAt',
        });
        all.push(...page.items);
        hasMore = page.hasMore;
        cursor = page.nextCursor;
      }
      return all;
    },
    get: async (companyId: string, positionId: string, id: string): Promise<Application | undefined> => {
      try {
        const raw = await apiGet<any>(`/companies/${companyId}/job-positions/${positionId}/applications/${id}`);
        return mapApplication(raw, companyId);
      } catch {
        return undefined;
      }
    },
    reject: async (
      companyId: string,
      positionId: string,
      id: string,
      note?: string,
      sendEmail?: boolean,
    ): Promise<Application> => {
      // Sprint 5 (S5.B): default sendEmail=true on the FE side too — the
      // dialog toggle defaults to checked so recruiters who just hit
      // "Confirm" send the polite rejection by default. We explicitly
      // forward `false` (not omit) so the BE coercion stays unambiguous.
      const raw = await apiPost<any>(
        `/companies/${companyId}/job-positions/${positionId}/applications/${id}/reject`,
        { note, sendEmail: sendEmail !== false },
      );
      return mapApplication(raw, companyId);
    },
    /**
     * Sprint 4 (S4.C) — recruiter-initiated manual application.
     *
     * Posts a multipart/form-data payload to the authenticated endpoint.
     * The recruiter has the candidate's CV out-of-band (email, WhatsApp,
     * referral) and wants to push it through the same pipeline (cap check,
     * dedup, AI screening, notifications) as the public form.
     *
     * `jobPostingId` is optional — when omitted, the BE picks the first
     * active posting on the position, so the simplest call site is just
     * `(companyId, positionId, { firstName, lastName, email, cv })`.
     */
    createManual: async (
      companyId: string,
      positionId: string,
      input: {
        firstName: string;
        lastName: string;
        email: string;
        cv: File;
        phone?: string;
        linkedinUrl?: string;
        jobPostingId?: string;
        source?: string;
      },
    ): Promise<{ applicationId: string; message: string }> => {
      const fd = new FormData();
      fd.append('firstName', input.firstName);
      fd.append('lastName', input.lastName);
      fd.append('email', input.email);
      if (input.phone) fd.append('phone', input.phone);
      if (input.linkedinUrl) fd.append('linkedinUrl', input.linkedinUrl);
      if (input.jobPostingId) fd.append('jobPostingId', input.jobPostingId);
      fd.append('source', input.source ?? 'manual_by_recruiter');
      fd.append('cv', input.cv, input.cv.name);
      const raw = await apiPost<any>(
        `/companies/${companyId}/job-positions/${positionId}/applications`,
        fd,
        // FormData → apiPost detects the body shape and skips JSON
        // serialization so the browser sets the multipart boundary.
      );
      return {
        applicationId: raw.applicationId ?? raw.id ?? '',
        message: raw.message ?? '',
      };
    },
    hire: async (companyId: string, positionId: string, id: string, note?: string): Promise<Application> => {
      const raw = await apiPost<any>(`/companies/${companyId}/job-positions/${positionId}/applications/${id}/hire`, { note });
      return mapApplication(raw, companyId);
    },
    decide: async (companyId: string, positionId: string, id: string, pipelineKey: string, decisionInputs?: any): Promise<Application> => {
      const raw = await apiPost<any>(`/companies/${companyId}/job-positions/${positionId}/applications/${id}/decide`, { pipelineKey, decisionInputs });
      return mapApplication(raw, companyId);
    },
    updateInterview: async (
      companyId: string,
      positionId: string,
      applicationId: string,
      pipelineRunId: string,
      body: {
        scheduledDate: string;
        participants: string[];
        durationMinutes: number;
        interviewType: string;
        notes?: string;
        sendCustomEmail?: boolean;
        sendGoogleInvite?: boolean;
        enableTranscription?: boolean;
        createGoogleCalendar?: boolean;
        externalMeetingUrl?: string;
      },
    ): Promise<Application> => {
      const raw = await apiPatch<any>(
        `/companies/${companyId}/job-positions/${positionId}/applications/${applicationId}/interviews/${pipelineRunId}`,
        body,
      );
      return mapApplication(raw, companyId);
    },
    /** POST — dispatch Recall bot to join the meeting now (same run). */
    postInterviewTranscriptionJoinNow: async (
      companyId: string,
      positionId: string,
      applicationId: string,
      pipelineRunId: string,
    ): Promise<Application> => {
      const raw = await apiPost<any>(
        `/companies/${companyId}/job-positions/${positionId}/applications/${applicationId}/interviews/${pipelineRunId}/transcription-bot/join-now`,
        {},
      );
      return mapApplication(raw, companyId);
    },
    cancelInterview: async (
      companyId: string,
      positionId: string,
      applicationId: string,
      pipelineRunId: string,
      body?: { sendCustomEmail?: boolean; sendGoogleInvite?: boolean; cancellationMessage?: string },
    ): Promise<Application> => {
      const raw = await apiDelete<any>(
        `/companies/${companyId}/job-positions/${positionId}/applications/${applicationId}/interviews/${pipelineRunId}`,
        body ?? {},
      );
      return mapApplication(raw, companyId);
    },
    withdraw: async (companyId: string, positionId: string, id: string): Promise<Application> => {
      const raw = await apiPost<any>(`/companies/${companyId}/job-positions/${positionId}/applications/${id}/withdraw`, {});
      return mapApplication(raw, companyId);
    },
    reactivate: async (companyId: string, positionId: string, id: string, note?: string): Promise<Application> => {
      const raw = await apiPost<any>(
        `/companies/${companyId}/job-positions/${positionId}/applications/${id}/reactivate`,
        note ? { note } : {},
      );
      return mapApplication(raw, companyId);
    },
    /** Start or re-run AI screening (also stops stuck PIPELINE_RUNNING and restarts). */
    processNow: async (companyId: string, positionId: string, id: string): Promise<Application> => {
      const raw = await apiPost<any>(
        `/companies/${companyId}/job-positions/${positionId}/applications/${id}/process-now`,
        {},
      );
      return mapApplication(raw, companyId);
    },
    /** Stop in-flight AI screening without re-running (no credits). */
    abortStuck: async (companyId: string, positionId: string, id: string): Promise<Application> => {
      const raw = await apiPost<any>(
        `/companies/${companyId}/job-positions/${positionId}/applications/${id}/abort-stuck`,
        {},
      );
      return mapApplication(raw, companyId);
    },
    /** Sequential bulk resume after plan upgrade (server-side loop). */
    processPending: async (companyId: string): Promise<{
      processed: number;
      skippedBilling: number;
      errors: number;
      total: number;
    }> => {
      return apiPost(`/companies/${companyId}/applications/process-pending`, {});
    },
    /** Phase 1 — stop stuck PIPELINE_RUNNING runs without re-running AI. */
    bulkAbortStuck: async (
      companyId: string,
      applications: Array<{ applicationId: string; positionId: string }>,
    ): Promise<{ aborted: number; skipped: number; errors: number; total: number }> => {
      return apiPost(`/companies/${companyId}/applications/bulk-abort-stuck`, { applications });
    },
    /** Phase 2 — queue screening sequentially (async pipeline trigger). */
    bulkProcessNow: async (
      companyId: string,
      applications: Array<{ applicationId: string; positionId: string }>,
    ): Promise<{
      processed: number;
      skippedBilling: number;
      errors: number;
      total: number;
    }> => {
      return apiPost(`/companies/${companyId}/applications/bulk-process-now`, { applications });
    },
    /** Lightweight poll for bulk queue — state + score without full application detail. */
    bulkProcessingStatus: async (
      companyId: string,
      applications: Array<{
        applicationId: string;
        positionId?: string;
        /** @deprecated Use positionId — BE validates `positionId` only. */
        jobPositionId?: string;
      }>,
    ): Promise<Array<{
      applicationId: string;
      jobPositionId: string;
      state: Application['state'];
      updatedAt: string;
      aiScore?: number;
      screeningQueued?: boolean;
    }>> => {
      const normalized = applications
        .map((a) => ({
          applicationId: a.applicationId,
          positionId: a.positionId ?? a.jobPositionId ?? '',
        }))
        .filter((a) => a.applicationId && a.positionId);

      const raw = await apiPost<{ items?: Array<{
        applicationId: string;
        jobPositionId: string;
        state: Application['state'];
        updatedAt: string;
        aiScore?: number;
        screeningQueued?: boolean;
      }> }>(
        `/companies/${companyId}/applications/bulk-processing-status`,
        { applications: normalized },
      );
      return raw.items ?? [];
    },
    /** Sync PENDING_UPGRADE → READY_TO_PROCESS when plan has capacity. */
    unlockDeferred: async (companyId: string): Promise<{
      unlocked: number;
      pendingUpgrade: number;
      readyToProcess: number;
      billingRetry: number;
    }> => {
      return apiPost(`/companies/${companyId}/applications/unlock-deferred`, {});
    },
    archivedCount: async (companyId: string): Promise<number> => {
      const { value, found } = await apiGetWithFound<{ count: number }>(
        `/companies/${companyId}/applications/archived-count`,
        { count: 0 },
      );
      if (!found) {
        markArchivedCountEndpointUnavailable(companyId);
        return 0;
      }
      clearArchivedCountEndpointUnavailable(companyId);
      return typeof value.count === 'number' ? value.count : 0;
    },
    activeCount: async (companyId: string): Promise<number> => {
      const { value, found } = await apiGetWithFound<{ count: number }>(
        `/companies/${companyId}/applications/active-count`,
        { count: 0 },
      );
      if (!found) {
        markActiveCountEndpointUnavailable(companyId);
        return 0;
      }
      clearActiveCountEndpointUnavailable(companyId);
      return typeof value.count === 'number' ? value.count : 0;
    },
    filteredCount: async (
      companyId: string,
      options: { archived: boolean; state?: string; jobPositionId?: string },
    ): Promise<number> => {
      const params = new URLSearchParams();
      params.set('archived', options.archived ? 'true' : 'false');
      if (options.state) params.set('state', options.state);
      if (options.jobPositionId) params.set('jobPositionId', options.jobPositionId);
      const qs = params.toString();
      const raw = await apiGet<{ count: number }>(
        `/companies/${companyId}/applications/filtered-count${qs ? `?${qs}` : ''}`,
      );
      return typeof raw.count === 'number' ? raw.count : 0;
    },
    archive: async (companyId: string, positionId: string, id: string): Promise<Application> => {
      const raw = await apiPost<any>(
        `/companies/${companyId}/job-positions/${positionId}/applications/${id}/archive`,
        {},
      );
      return mapApplication(raw, companyId);
    },
    unarchive: async (companyId: string, positionId: string, id: string): Promise<Application> => {
      const raw = await apiPost<any>(
        `/companies/${companyId}/job-positions/${positionId}/applications/${id}/unarchive`,
        {},
      );
      return mapApplication(raw, companyId);
    },
    bulkArchive: async (
      companyId: string,
      applications: Array<{ applicationId: string; positionId: string }>,
    ): Promise<{ updated: number; errors: number; total: number }> => {
      try {
        return await apiPost(`/companies/${companyId}/applications/bulk-archive`, { applications });
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 404) throw err;
        return bulkArchiveFallback(companyId, applications);
      }
    },
    bulkUnarchive: async (
      companyId: string,
      applications: Array<{ applicationId: string; positionId: string }>,
    ): Promise<{ updated: number; errors: number; total: number }> => {
      try {
        return await apiPost(`/companies/${companyId}/applications/bulk-unarchive`, { applications });
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 404) throw err;
        return bulkUnarchiveFallback(companyId, applications);
      }
    },
    getAvailableActions: async (companyId: string, positionId: string, id: string): Promise<AvailableAction[]> => {
      const raw = await apiGet<{ options: AvailableAction[] }>(`/companies/${companyId}/job-positions/${positionId}/applications/${id}/available-actions`);
      return raw.options || [];
    },
    listDocuments: async (companyId: string, positionId: string, id: string): Promise<{ id: string; type: string; fileName: string; mimeType: string; createdAt: any; data?: Record<string, unknown>; downloadUrl: string; contentUrl: string }[]> => {
      try {
        const raw = await apiGet<any>(`/companies/${companyId}/job-positions/${positionId}/applications/${id}/documents`);
        return raw.documents || [];
      } catch {
        return [];
      }
    },
    downloadDocumentById: async (companyId: string, positionId: string, appId: string, docId: string): Promise<Blob> => {
      return apiGetBlob(`/companies/${companyId}/job-positions/${positionId}/applications/${appId}/documents/${docId}/download`);
    },
    downloadCvPdf: async (companyId: string, positionId: string, id: string): Promise<Blob> => {
      // Find resume_document in documents list and download it
      const docs = await api.applications.listDocuments(companyId, positionId, id);
      const resumeDoc = docs.find(d => d.type === 'resume_document');
      if (resumeDoc) {
        return apiGetBlob(`/companies/${companyId}/job-positions/${positionId}/applications/${id}/documents/${resumeDoc.id}/download`);
      }
      throw new Error('No resume document found');
    },
    getScreeningReports: async (companyId: string, positionId: string, id: string): Promise<ScreeningReport[]> => {
      try {
        // New document-based approach: report_screening documents have embedded data in Firestore
        const docs = await api.applications.listDocuments(companyId, positionId, id);
        const reportDocs = docs.filter(d => d.type === 'report_screening' && d.data);
        if (reportDocs.length > 0) {
          return reportDocs.map((d, i) => {
            const r = d.data as any;
            const rawTrigger = r?.trigger ?? 'application';
            const trigger: ScreeningReport['trigger'] =
              rawTrigger === 'interview' || rawTrigger === 'interview_completed'
                ? 'interview_completed'
                : rawTrigger === 'manual'
                  ? 'manual'
                  : 'application';
            return {
              id: d.id,
              version: r?.version ?? i + 1,
              generatedAt: d.createdAt?._seconds
                ? new Date(d.createdAt._seconds * 1000).toISOString()
                : (d.createdAt as string) ?? new Date().toISOString(),
              trigger,
              triggerLabel: r?.triggerLabel ?? 'Initial screening',
              basedOnDocuments: r?.basedOnDocuments || [{ type: 'cv' as const, label: 'Resume' }],
              overallScore: r?.overallScore ?? 0,
              scoreDelta: r?.scoreDelta,
              summary: r?.summary || '',
              // New format: {text, importance}[]. Legacy fallback: merge pros/cons strings as importance=2
              strengths: r?.strengths?.length
                ? r.strengths
                : (r?.pros || []).map((p: string) => ({ text: p, importance: 2 as const })),
              weaknesses: r?.weaknesses?.length
                ? r.weaknesses
                : (r?.cons || []).map((c: string) => ({ text: c, importance: 2 as const })),
              missingData: r?.missingData?.length && typeof r.missingData[0] === 'object'
                ? r.missingData
                : (r?.missingData || []).map((m: string) => ({ text: m, importance: 2 as const })),
              suggestedQuestions: r?.suggestedQuestions || [],
            } satisfies ScreeningReport;
          });
        }
        // Fallback: extract from application object (collectedData / legacy fields)
        const raw = await apiGet<any>(`/companies/${companyId}/job-positions/${positionId}/applications/${id}`);
        const app = mapApplication(raw, companyId);
        return app.screeningReports || [];
      } catch {
        return [];
      }
    },
    getCvContent: async (companyId: string, positionId: string, id: string): Promise<string | null> => {
      try {
        const docs = await api.applications.listDocuments(companyId, positionId, id);
        const contentDoc = docs.find(d => d.type === 'resume_content');
        if (!contentDoc) return null;
        const blob = await apiGetBlob(`/companies/${companyId}/job-positions/${positionId}/applications/${id}/documents/${contentDoc.id}/content`);
        return blob.text();
      } catch {
        return null;
      }
    },
    getStructuredData: async (companyId: string, positionId: string, id: string): Promise<CandidateStructuredData | null> => {
      try {
        const docs = await api.applications.listDocuments(companyId, positionId, id);
        const structuredDoc = docs.find(d => d.type === 'resume_structured');
        if (!structuredDoc) return null;
        // Prefer embedded data (avoids extra GCS call)
        const raw = structuredDoc.data
          ? (structuredDoc.data as unknown as Record<string, unknown>)
          : await (async () => {
              const blob = await apiGetBlob(`/companies/${companyId}/job-positions/${positionId}/applications/${id}/documents/${structuredDoc.id}/content`);
              const text = await blob.text();
              return JSON.parse(text) as Record<string, unknown>;
            })();
        return normalizeCandidateStructured(raw);
      } catch {
        return null;
      }
    },
    getPipelineDetail: async (companyId: string, positionId: string, appId: string): Promise<PipelineDetailRun[]> => {
      try {
        const raw = await apiGet<any>(`/companies/${companyId}/job-positions/${positionId}/applications/${appId}/pipeline-detail`);
        return raw.pipelineRuns || [];
      } catch {
        return [];
      }
    },
  },

  team: {
    list: async (companyId: string): Promise<TeamMember[]> => {
      const raw = await apiGet<any>(`/companies/${companyId}/members`);
      return extractItems(raw).map(mapTeamMember);
    },
    add: async (companyId: string, data: {
      /** XOR with email — Firebase Auth UID */
      authUid?: string;
      /** XOR with authUid — invitee email (resolved via Admin SDK) */
      email?: string;
      displayName?: string;
      /** System permission level for the new member */
      permission: 'admin' | 'write' | 'read';
      type: string;
      teamIds?: string[];
      /** Free-form organizational role label */
      jobRole?: string | null;
      showInSuggestions?: boolean;
    }): Promise<TeamMember> => {
      const raw = await apiPost<any>(`/companies/${companyId}/members`, data);
      return mapTeamMember(raw);
    },
    update: async (companyId: string, memberId: string, data: {
      permission?: 'admin' | 'write' | 'read';
      type?: string;
      isActive?: boolean;
      /** Free-form organizational role label */
      jobRole?: string | null;
      showInSuggestions?: boolean;
    }): Promise<TeamMember> => {
      const raw = await apiPatch<any>(`/companies/${companyId}/members/${memberId}`, data);
      return mapTeamMember(raw);
    },
    // PATCH /companies/:cid/members/:memberId/teams — replace full team list (v5.1)
    assignTeams: async (companyId: string, memberId: string, teamIds: string[]): Promise<TeamMember> => {
      const raw = await apiPatch<any>(`/companies/${companyId}/members/${memberId}/teams`, { teamIds });
      return mapTeamMember(raw);
    },
    remove: async (companyId: string, memberId: string): Promise<void> => {
      await apiDelete(`/companies/${companyId}/members/${memberId}`);
    },
  },

  dashboard: {
    get: async (companyId: string): Promise<any> => {
      try {
        return await apiGet<any>(`/companies/${companyId}/dashboard`);
      } catch {
        return null;
      }
    },
  },

  ai: {
    /**
     * chat — POST /companies/:companyId/ai/chat
     *
     * Multi-turn conversational AI for job position assistant.
     * Sends the user message and optional conversation state to the BE.
     * Returns AI response text, UX status messages, and updated conversation state.
     *
     * Demo mode: throws an error — AI features are not available in demo.
     */
    chat: async (
      companyId: string,
      req: AiChatRequest
    ): Promise<AiChatResponse> => {
      // silenceUpgradeWall: AI chat callers (Job Position / Application detail
      // assistants) already render the upgrade hint inline as a chat message
      // (see `billing.aiNotEnabled` i18n key). Suppressing the global
      // UpgradeWallModal avoids the duplicated pop-up reported by Alessandro.
      return apiPost<AiChatResponse>(
        `/companies/${companyId}/ai/chat`,
        req,
        { silenceUpgradeWall: true }
      );
    },

    /**
     * autofill — POST /companies/:companyId/ai/autofill
     *
     * Single-turn structured field extraction from conversation history.
     * Sends the current conversationState and returns { suggestions: AiAutoFillSuggestions }.
     * FE shows the suggestions in a preview before applying to the form.
     *
     * Demo mode: throws an error — AI features are not available in demo.
     */
    autofill: async (
      companyId: string,
      req: AiAutoFillRequest
    ): Promise<AiAutoFillResponse> => {
      return apiPost<AiAutoFillResponse>(
        `/companies/${companyId}/ai/autofill`,
        req,
        { silenceUpgradeWall: true }
      );
    },
  },

  public: {
    getPosting: async (companySlug: string, postingId: string): Promise<{ company: Company; position: JobPosition; posting: JobPosting } | null> => {
      try {
        const raw = await publicGet<any>(`/public/${companySlug}/${postingId}`);
        const posRaw = raw.position || {};
        return {
          company: {
            id: raw.companyId || raw.company?.id || '',
            slug: raw.companySlug || companySlug || '',
            publicName: raw.company?.publicName || '',
            description: raw.company?.description,
            logoUrl: raw.company?.logoUrl || null,
            employeeCount: raw.company?.employeeCount,
          } as Company,
          position: mapPosition({
            ...posRaw,
            id: raw.jobPositionId || posRaw.id || '',
            companyId: raw.companyId || posRaw.companyId || '',
            title: raw.positionTitle || posRaw.title || raw.title || '',
            description: raw.description || posRaw.description || '',
            applicationFields: raw.applicationFields || posRaw.applicationFields,
            outputLanguage: raw.outputLanguage,
          }),
          posting: mapPosting({
            id: raw.postingId || raw.jobPostingId || '',
            jobPositionId: raw.jobPositionId || '',
            positionId: raw.jobPositionId || '',
            positionTitle: raw.title || '',
            isActive: true,
            landingPageUrl: toPublicUrl(raw.landingPageUrl || ''),
            outputLanguage: raw.outputLanguage,
            channel: 'Other',
          }),
        };
      } catch {
        return null;
      }
    },
    submitApplication: submitCandidateApplication,
    submitContact: (data: {
      name: string;
      email: string;
      company?: string;
      type: 'general' | 'agency' | 'demo';
      message: string;
      website?: string;
      locale?: 'it' | 'en';
    }) => publicPost<{ ok: true }>('/public/contact', data),
  },

  // ---------------------------------------------------------------------------
  // Invitations — public + authenticated endpoints
  // ---------------------------------------------------------------------------
  invitations: {
    /**
     * create — POST /companies/:cid/invitations
     * Sends an invitation email via the pipeline. Requires write+ permission.
     * Returns { invitationId, status }.
     */
    async create(companyId: string, data: {
      email: string;
      /** Sprint 6 (S6.A) — optional pre-fill so the email greets by name. */
      firstName?: string;
      lastName?: string;
      permission: 'admin' | 'write' | 'read';
      type: 'internal' | 'external';
      jobRole?: string | null;
      /**
       * Teams the new member should be assigned to on accept (Plan E
       * Phase 1 Task 1.4). Persisted on the invitation doc and
       * replayed by the BE when the membership is materialized.
       */
      teamIds?: string[];
    }): Promise<{ invitationId: string; status: string }> {
      return apiPost(`/companies/${companyId}/invitations`, data);
    },
    /**
     * getPublic — fetches public invitation data.
     * Two separate params mirror /public/:companySlug/:postingId pattern.
     * No auth required.
     */
    async getPublic(invitationId: string, token: string): Promise<{
      invitationId: string;
      companyName: string;
      companyPublicName: string;
      companyLogoUrl?: string;
      permission: string;
      jobRole: string | null;
      type: string;
      invitedByEmail: string;
      expiresAt: { _seconds: number } | string;
      status: string;
    }> {
      return publicGet(`/public/invitations/${invitationId}/${token}`);
    },

    /**
     * Sprint 6 (S6.B) — renew an expired or pending invitation.
     *
     * Calls POST /companies/:cid/invitations/:invitationId/renew (admin only)
     * which regenerates the token + expiresAt and re-sends the email via the
     * pipeline. Used by the "Resend" action in the members list when an
     * invitation has gone stale.
     */
    async renew(companyId: string, invitationId: string): Promise<{ invitationId: string; status: string }> {
      return apiPost(`/companies/${companyId}/invitations/${invitationId}/renew`, {});
    },

    /**
     * respond — records accept/decline decision.
     * No auth required.
     */
    async respond(invitationId: string, token: string, action: 'accept' | 'decline'): Promise<void> {
      const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://europe-west3-recruiterai-8d65b.cloudfunctions.net/api/v1';
      const res = await fetch(`${BASE_URL}/public/invitations/${invitationId}/${token}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      if (!res.ok) {
        let message = `Request failed with status ${res.status}`;
        try {
          const err = await res.json() as { message?: string };
          message = err.message || message;
        } catch { /* ignore */ }
        throw Object.assign(new Error(message), { status: res.status });
      }
    },

    /**
     * complete — confirms acceptance after login/signup.
     * Requires Firebase Bearer token (pass explicitly).
     */
    async complete(invitationId: string, token: string, bearerToken: string): Promise<{ memberId: string; companyId: string }> {
      const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://europe-west3-recruiterai-8d65b.cloudfunctions.net/api/v1';
      const res = await fetch(`${BASE_URL}/public/invitations/${invitationId}/${token}/complete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${bearerToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        let message = `Request failed with status ${res.status}`;
        try {
          const err = await res.json() as { message?: string };
          message = err.message || message;
        } catch { /* ignore */ }
        throw Object.assign(new Error(message), { status: res.status });
      }
      const json = await res.json();
      return json.data ?? json;
    },
  },
};
